import { useCallback, useEffect, useState } from 'react';
import { Check, RotateCcw, X } from 'lucide-react';
import { Trans, useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import apiClient from '../api/client';
import { useToast } from './ui/Toast';
import { inputClass } from './ui/Input';
import { usePermission } from '../auth/usePermission';
import { PermissionName } from '../auth/permissions';
import { formatEUR } from '../lib/format';
import {
  coefficientFromMargin,
  fmtCoefficient,
  isSellableCoefficient,
  marginFromCoefficient,
  parseNum,
} from '../lib/margin';

/**
 * The INHERITANCE ROW — what this article sells at for each customer family, and where that
 * coefficient came from.
 *
 * The reference ERP the counter already uses has a row for exactly this, and it is the answer
 * to the question an operator disputing a price actually asks: not "what is the coefficient"
 * but "why is it that one". So every line names its origin — inherited from the brand, from the
 * article family, or overridden here — and the override is editable in place, because the
 * moment you can see that a family is wrong is the moment you want to fix it.
 *
 * The figures come from /price-grids/product/:id, which resolves through the SAME service that
 * prices the sale line. A second implementation here would eventually disagree with the invoice,
 * and this panel exists precisely to be believed.
 */

type Level =
  | 'PRODUCT'
  | 'PRODUCT_COEFFICIENT'
  | 'BRAND_FAMILY'
  | 'BRAND'
  | 'SUPPLIER_FAMILY'
  | 'SUPPLIER'
  | 'ARTICLE_FAMILY'
  | 'CUSTOMER_ALL'
  | 'FAMILY_DEFAULT';

interface FamilyCoefficient {
  priceGridId: string;
  code: string;
  name: string;
  coefficient: number;
  marginPct: number | null;
  level: Level;
  ruleId: string | null;
  source: string;
  /** The quantity bracket the figure belongs to. This panel always asks for one unit. */
  qtyMin: number;
  qtyMax: number | null;
  sellingPriceHT: number;
}

interface Payload {
  productId: string;
  brand: string | null;
  /** Always 1 here: this panel answers "what does ONE of these cost each family". */
  quantity: number;
  purchasePriceHT: number;
  productCoefficient: number | null;
  families: FamilyCoefficient[];
}

/** `label` is an i18n key under productAdmin.coefficients.levels, resolved at render time. */
const LEVEL_BADGE: Record<Level, { label: string; cls: string }> = {
  PRODUCT: { label: 'PRODUCT', cls: 'bg-amber-500/15 text-amber-600' },
  PRODUCT_COEFFICIENT: { label: 'PRODUCT_COEFFICIENT', cls: 'bg-amber-500/15 text-amber-600' },
  BRAND_FAMILY: { label: 'BRAND_FAMILY', cls: 'bg-primary/10 text-primary' },
  BRAND: { label: 'BRAND', cls: 'bg-success/15 text-success' },
  SUPPLIER_FAMILY: { label: 'SUPPLIER_FAMILY', cls: 'bg-violet-500/15 text-violet-600' },
  SUPPLIER: { label: 'SUPPLIER', cls: 'bg-violet-500/15 text-violet-600' },
  ARTICLE_FAMILY: { label: 'ARTICLE_FAMILY', cls: 'bg-blue-500/15 text-blue-600' },
  CUSTOMER_ALL: { label: 'CUSTOMER_ALL', cls: 'bg-rose-500/15 text-rose-600' },
  FAMILY_DEFAULT: { label: 'FAMILY_DEFAULT', cls: 'bg-muted text-muted-foreground' },
};

/** "1 and up" or "1–9" — what quantities the winning cell covers. */
const bracketNote = (t: TFunction, min: number, max: number | null): string | null =>
  min === 1 && max == null
    ? null
    : max == null
      ? t('productAdmin.coefficients.bracketOpen', { min })
      : t('productAdmin.coefficients.bracketRange', { min, max });

const cmd = () => ({ headers: { 'X-Command-Id': crypto.randomUUID() } });

export function ProductCoefficientInheritance({ productId }: { productId: string | null }) {
  const { t } = useTranslation();
  const { showApiError, push } = useToast();
  const canWrite = usePermission(PermissionName.PRICING_GRIDS_RW);
  const [data, setData] = useState<Payload | null>(null);
  const [failed, setFailed] = useState(false);
  // Which of the two linked figures is being typed. Both cells are editable, because the
  // counter thinks in margin and the catalogue thinks in coefficient, and forcing one of them to
  // convert in their head is how ×1.45 gets typed where ×1.8182 was meant.
  const [editing, setEditing] = useState<{
    familyId: string;
    unit: 'COEF' | 'MARGIN';
    value: string;
  } | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!productId) return;
    try {
      const { data: res } = await apiClient.get<{ data: Payload }>(
        `/price-grids/product/${productId}`
      );
      setData(res.data);
      setFailed(false);
    } catch {
      // A panel that cannot load must not take the product form down with it.
      setFailed(true);
    }
  }, [productId]);

  useEffect(() => {
    void load();
  }, [load]);

  // An unsaved product has no id, so there is no product row for a per-family override to hang
  // off yet. Say so rather than rendering an empty box.
  if (!productId) {
    return (
      <p className="text-xs text-muted-foreground">{t('productAdmin.coefficients.saveFirst')}</p>
    );
  }
  if (failed || !data) return null;
  if (data.families.length === 0) {
    return (
      <p className="text-xs text-muted-foreground">{t('productAdmin.coefficients.noFamily')}</p>
    );
  }

  const save = async () => {
    if (!editing) return;
    const raw = editing.value.trim();
    setBusy(true);
    try {
      if (raw === '') {
        const row = data.families.find((f) => f.priceGridId === editing.familyId);
        if (row?.ruleId) await apiClient.delete(`/price-grids/rules/${row.ruleId}`, cmd());
      } else {
        const n = parseNum(raw);
        const coefficient =
          n == null ? null : editing.unit === 'COEF' ? n : coefficientFromMargin(n);
        if (coefficient == null || !isSellableCoefficient(coefficient)) {
          push({
            tone: 'error',
            message:
              editing.unit === 'COEF'
                ? t('productAdmin.coefficients.invalidCoefficient')
                : t('productAdmin.coefficients.invalidMargin'),
          });
          setBusy(false);
          return;
        }
        await apiClient.put(
          '/price-grids/rules',
          { priceGridId: editing.familyId, productId, coefficient },
          cmd()
        );
      }
      setEditing(null);
      await load();
    } catch (err) {
      showApiError(err);
    } finally {
      setBusy(false);
    }
  };

  const clearOverride = async (row: FamilyCoefficient) => {
    if (!row.ruleId) return;
    setBusy(true);
    try {
      await apiClient.delete(`/price-grids/rules/${row.ruleId}`, cmd());
      await load();
    } catch (err) {
      showApiError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 text-xs text-muted-foreground">
        <span>
          {t('productAdmin.coefficients.netPurchase')}{' '}
          <strong className="tabular-nums">{formatEUR(data.purchasePriceHT)} HT</strong>{' '}
          {t('productAdmin.coefficients.formula')}
        </span>
        {data.productCoefficient != null && (
          <span className="text-amber-600">
            {t('productAdmin.coefficients.ownCoefficient', {
              coefficient: fmtCoefficient(data.productCoefficient),
            })}
          </span>
        )}
      </div>

      <div className="overflow-hidden rounded-md border">
        <table className="w-full text-xs">
          <thead className="bg-muted/50 text-muted-foreground">
            <tr>
              <th className="px-2 py-1.5 text-left font-medium">
                {t('productAdmin.coefficients.columns.family')}
              </th>
              <th className="px-2 py-1.5 text-right font-medium">
                {t('productAdmin.coefficients.columns.coefficient')}
              </th>
              <th
                className="px-2 py-1.5 text-right font-medium"
                title={t('productAdmin.coefficients.columns.marginTitle')}
              >
                {t('productAdmin.coefficients.columns.margin')}
              </th>
              <th className="px-2 py-1.5 text-right font-medium">
                {t('productAdmin.coefficients.columns.sellsAt')}
              </th>
              <th className="px-2 py-1.5 text-left font-medium">
                {t('productAdmin.coefficients.columns.comesFrom')}
              </th>
              {canWrite && <th className="w-8 px-2 py-1.5" />}
            </tr>
          </thead>
          <tbody>
            {data.families.map((f) => {
              const isEditing = editing?.familyId === f.priceGridId;
              const badge = LEVEL_BADGE[f.level];
              const overridden = f.level === 'PRODUCT';
              return (
                <tr key={f.priceGridId} className="border-t">
                  <td className="px-2 py-1.5">
                    <span className="font-medium">{f.code}</span>
                    <span className="ml-1 text-muted-foreground">{f.name}</span>
                  </td>
                  <td className="px-2 py-1.5 text-right tabular-nums">
                    {isEditing && editing.unit === 'COEF' ? (
                      <div className="flex items-center justify-end gap-1">
                        <input
                          autoFocus
                          value={editing.value}
                          onChange={(e) => setEditing({ ...editing, value: e.target.value })}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') save();
                            if (e.key === 'Escape') setEditing(null);
                          }}
                          placeholder={t('productAdmin.coefficients.inherit')}
                          className={inputClass + ' h-7 w-24 px-1.5 text-right'}
                        />
                        <button
                          onClick={save}
                          disabled={busy}
                          className="text-success hover:opacity-70"
                          title={t('productAdmin.coefficients.save')}
                        >
                          <Check className="h-3.5 w-3.5" />
                        </button>
                        <button
                          onClick={() => setEditing(null)}
                          className="text-muted-foreground hover:text-foreground"
                          title={t('productAdmin.coefficients.cancel')}
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        disabled={!canWrite}
                        onClick={() =>
                          setEditing({
                            familyId: f.priceGridId,
                            unit: 'COEF',
                            // Prefill only a real override: prefilling the inherited figure would
                            // turn "leave it alone" into "pin it here" on the next Enter.
                            value: overridden ? fmtCoefficient(f.coefficient) : '',
                          })
                        }
                        className={canWrite ? 'hover:underline' : ''}
                        title={canWrite ? t('productAdmin.coefficients.overrideFamily') : undefined}
                      >
                        ×{fmtCoefficient(f.coefficient)}
                      </button>
                    )}
                  </td>
                  <td className="px-2 py-1.5 text-right tabular-nums">
                    {isEditing && editing.unit === 'MARGIN' ? (
                      <div className="flex items-center justify-end gap-1">
                        <input
                          autoFocus
                          value={editing.value}
                          onChange={(e) => setEditing({ ...editing, value: e.target.value })}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') save();
                            if (e.key === 'Escape') setEditing(null);
                          }}
                          placeholder={t('productAdmin.coefficients.inherit')}
                          className={inputClass + ' h-7 w-20 px-1.5 text-right'}
                        />
                        <button
                          onClick={save}
                          disabled={busy}
                          className="text-success hover:opacity-70"
                          title={t('productAdmin.coefficients.save')}
                        >
                          <Check className="h-3.5 w-3.5" />
                        </button>
                        <button
                          onClick={() => setEditing(null)}
                          className="text-muted-foreground hover:text-foreground"
                          title={t('productAdmin.coefficients.cancel')}
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        disabled={!canWrite}
                        onClick={() =>
                          setEditing({
                            familyId: f.priceGridId,
                            unit: 'MARGIN',
                            value: overridden
                              ? String(marginFromCoefficient(f.coefficient) ?? '')
                              : '',
                          })
                        }
                        className={canWrite ? 'hover:underline' : ''}
                        title={
                          canWrite ? t('productAdmin.coefficients.overrideByMargin') : undefined
                        }
                      >
                        {marginFromCoefficient(f.coefficient)?.toFixed(2)} %
                      </button>
                    )}
                  </td>
                  <td className="px-2 py-1.5 text-right tabular-nums font-medium">
                    {formatEUR(f.sellingPriceHT)}
                  </td>
                  <td className="px-2 py-1.5">
                    <span className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${badge.cls}`}>
                      {t(`productAdmin.coefficients.levels.${badge.label}`)}
                    </span>
                    <span className="ml-1 text-muted-foreground">{f.source}</span>
                    {/* Only when it is not "1 and up": a bracket that covers everything is not
                        a fact about this line, it is the absence of one. */}
                    {bracketNote(t, f.qtyMin, f.qtyMax) && (
                      <span className="ml-1 rounded bg-muted px-1 py-px text-[10px] tabular-nums text-muted-foreground">
                        {bracketNote(t, f.qtyMin, f.qtyMax)}
                      </span>
                    )}
                  </td>
                  {canWrite && (
                    <td className="px-2 py-1.5 text-right">
                      {overridden && (
                        <button
                          onClick={() => clearOverride(f)}
                          disabled={busy}
                          className="p-0.5 text-muted-foreground hover:text-destructive"
                          title={t('productAdmin.coefficients.removeOverride')}
                        >
                          <RotateCcw className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {canWrite && (
        <p className="text-[11px] text-muted-foreground">
          <Trans i18nKey="productAdmin.coefficients.help" components={{ em: <em /> }} />
        </p>
      )}
    </div>
  );
}
